/**
 * The ways a business in a country is paid, so setup never asks "How do
 * customers pay?" when it knows where the business is (owner, 30 Oct 2026:
 * an Indian hotel that had said "cash, UPI or card" was asked it three
 * times). A sale record that does not say how it was paid gets "Paid by" with
 * these choices, most used first; with the country unknown nothing is added,
 * and the setup's question stays the one that asks.
 *
 * Each label is one `modeAccountOf` reads as money the business holds: Cash,
 * the bank, or a wallet of its own ("Wallet (M-Pesa)"), so a rule built on
 * them never sends a payment to To sort.
 */
import type { StudioDraft, StudioRecord } from './draft';
import { accountsListOf, isPlaceLabel, postingOf } from './money-rules';
import type { StudioOp } from './ops';

/** The common ways to be paid, by ISO country code, most used first; at most six. */
const MODES: Readonly<Record<string, readonly string[]>> = {
  IN: ['Cash', 'UPI', 'Card', 'Bank transfer', 'Cheque'],
  PK: ['Cash', 'Bank transfer', 'Wallet (JazzCash, Easypaisa)', 'Card', 'Cheque'],
  BD: ['Cash', 'Wallet (bKash, Nagad)', 'Card', 'Bank transfer', 'Cheque'],
  NP: ['Cash', 'Wallet (eSewa, Khalti)', 'QR (Fonepay)', 'Card', 'Bank transfer'],
  LK: ['Cash', 'Card', 'Bank transfer', 'Cheque'],
  AE: ['Cash', 'Card', 'Bank transfer', 'Cheque'],
  SA: ['Cash', 'Card (mada)', 'Bank transfer', 'Wallet (STC Pay)'],
  GB: ['Card', 'Cash', 'Bank transfer'],
  US: ['Card', 'Cash', 'Check', 'Bank transfer', 'Wallet (Zelle, Venmo)'],
  ID: ['Cash', 'QR (QRIS)', 'Bank transfer', 'Wallet (GoPay, OVO)', 'Card'],
  PH: ['Cash', 'Wallet (GCash, Maya)', 'Card', 'Bank transfer'],
  NG: ['Cash', 'Bank transfer', 'Card (POS)'],
  KE: ['Cash', 'Wallet (M-Pesa)', 'Card', 'Bank transfer'],
  SG: ['Cash', 'QR (PayNow)', 'Card', 'Bank transfer'],
  MY: ['Cash', 'QR (DuitNow)', 'Card', 'Bank transfer', "Wallet (Touch 'n Go)"],
};

/** The common ways to be paid in a country, or null when it is not known. */
export const paymentModesOf = (country: string | null | undefined): readonly string[] | null => (country === null || country === undefined ? null : (MODES[country.toUpperCase()] ?? null));

/** The countries the table knows, for its test. */
export const PAYMENT_MODE_COUNTRIES: readonly string[] = Object.keys(MODES);

const PAYMENT = /\b(cash|upi|cards?|mode|paid by|payments?|how paid|tunai|qris|cara bayar|pembayaran)\b/i;
/** A money field that is one mode's part of the day: Cash amount, UPI amount, Uang tunai (M1). */
const MODE_PART = /\b(cash|upi|card|tunai|qris|e-?wallet)\b/i;

/**
 * Whether an entry record says how it was paid: a choice of modes (by its
 * label, its key or its options), a pick of a list of modes, a pick of the
 * Accounts list named for where the money is (the money kit's "Received
 * in"), or a money field for each mode's part (Uang tunai, Uang QRIS).
 */
export const saysHowPaid = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord): boolean => {
  const accounts = accountsListOf(draft)?.key;
  const modeLists = new Set(draft.records.filter((one) => one.kind === 'directory' && PAYMENT.test(one.name)).map((one) => one.key));
  const fields = record.fields.flatMap((field) => [field, ...((field.lines?.fields ?? []) as typeof record.fields)]);
  return fields.some(
    (field) =>
      (field.type === 'select' && (PAYMENT.test(field.label) || PAYMENT.test(field.key.replace(/_/g, ' ')) || (field.options ?? []).some((option) => PAYMENT.test(option.label)))) ||
      (field.type === 'money' && MODE_PART.test(field.label)) ||
      ((field.type === 'reference' || field.type === 'multi_select') &&
        field.targetRecordKey !== undefined &&
        (modeLists.has(field.targetRecordKey) || (field.targetRecordKey === accounts && isPlaceLabel(field.label)))),
  );
};

const isMoney = (type: string, result?: string): boolean => type === 'money' || (type === 'formula' && result === 'money');

/**
 * A bill to a party picked from a list that says nothing of how it is paid
 * (gap B8 of docs/plans/gym-membership.md): a sale or a purchase whose rule
 * picks its party from a list and names no place paid, no part paid now and
 * no payment lines. It is paid by that list's payments record (D1, owner
 * 31 Oct: one payment for many bills, as Vyapar and Zoho do), so it is a bill,
 * never paid at once: it gets no Paid by of its own (`paymentModeOps`), and
 * the money pass adds the payments record (`settlePathOps`). A gym's
 * Memberships is one: each membership is owed by its member until a Member
 * payment takes it (2 Nov: Paid by on Memberships was read by nothing, so a
 * membership paid by card stayed owed and could not be paid).
 */
export const billToListParty = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord): boolean => {
  const rule = postingOf(record);
  if (rule === null || rule.byChoice !== undefined || (rule.pattern !== 'sale' && rule.pattern !== 'purchase')) return false;
  const roles = rule.roles ?? {};
  if (['paid_to', 'paid_from', 'paid_now', 'payments'].some((role) => roles[role] !== undefined)) return false;
  const party = roles.party;
  if (party === undefined || !('fieldKey' in party)) return false;
  const pick = record.fields.find((field) => field.key === party.fieldKey && field.type === 'reference');
  return draft.records.find((one) => one.key === pick?.targetRecordKey)?.kind === 'directory';
};

/**
 * The ops that give each sale record with money and no word of how it was
 * paid a "Paid by" of the country's modes. Nothing when the country is not
 * known: then the setup asks.
 */
export const paymentModeOps = (draft: Pick<StudioDraft, 'records'>, country: string | null | undefined): StudioOp[] => {
  const modes = paymentModesOf(country);
  if (modes === null) return [];
  const ops: StudioOp[] = [];
  for (const record of draft.records) {
    if (record.kind !== 'record' || record.direction !== 'in' || record.accounts !== undefined) continue;
    if (!record.fields.some((field) => isMoney(field.type, field.formula?.result))) continue;
    if (saysHowPaid(draft, record) || billToListParty(draft, record)) continue;
    let key = 'paid_by';
    for (let n = 2; record.fields.some((field) => field.key === key); n += 1) key = `paid_by_${n}`;
    ops.push({
      op: 'add_field',
      record: record.key,
      field: { key, label: 'Paid by', type: 'select', options: modes.map((label) => ({ key: label.toLowerCase().replace(/\W+/g, '_').replace(/^_|_$/g, ''), label })) },
    });
  }
  return ops;
};
